import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import type { Address } from 'viem'
import { useConfig } from 'wagmi'

import { QUERY_REFETCH_INTERVAL } from '@/config/consts'
import { useAccount } from '@/domain/hooks/useAccount'
import type { MarketInfo } from '../market-info/marketInfo'
import { useMarketInfo, useMarketInfoQuery } from '../market-info/useMarketInfo'
import { NormalizedUnitNumber } from '../types/NumericValues'
import { Token } from '../types/Token'
import { TokenSymbol } from '../types/TokenSymbol'
import { type BalanceItem, marketBalances } from './marketBalances'

export interface WalletBalance {
  balance: NormalizedUnitNumber
  token: Token
}

export interface MarketWalletInfoParams {
  chainId: number
  refetchInterval?: number
}

export interface MarketWalletInfoForMarketParams extends MarketWalletInfoParams {
  marketInfo: MarketInfo | undefined
}

export interface MarketWalletInfo {
  isConnected: boolean
  isLoading: boolean
  walletBalances: WalletBalance[]

  findWalletBalanceForToken: (token: Token) => NormalizedUnitNumber
  findWalletBalanceForSymbol: (symbol: TokenSymbol) => NormalizedUnitNumber
  findWalletBalanceForHyperCoreAsset: (symbol: TokenSymbol) => NormalizedUnitNumber
  getTotalUSD: () => NormalizedUnitNumber
  getTotalFormattedUSD: (options?: Intl.NumberFormatOptions) => string
}

export function useMarketWalletInfo({ chainId, refetchInterval }: MarketWalletInfoParams): MarketWalletInfo {
  const account = useAccount()
  const wagmiConfig = useConfig()
  const { marketInfo } = useMarketInfo({
    chainId,
    refetchInterval: typeof refetchInterval === 'number' ? refetchInterval : QUERY_REFETCH_INTERVAL,
  })

  const { data: balanceData } = useSuspenseQuery({
    ...marketBalances({
      wagmiConfig,
      account,
      chainId,
    }),
  })

  return useMarketWalletInfoFromData({ account, balanceData, marketInfo, isLoading: false })
}

export function useMarketWalletInfoQuery({ chainId, refetchInterval }: MarketWalletInfoParams): MarketWalletInfo {
  const account = useAccount()
  const wagmiConfig = useConfig()
  const { marketInfo, isPending: isMarketInfoPending } = useMarketInfoQuery({
    chainId,
    refetchInterval: typeof refetchInterval === 'number' ? refetchInterval : QUERY_REFETCH_INTERVAL,
  })

  const { data: balanceData = [], isPending: isBalancesPending } = useQuery({
    ...marketBalances({
      wagmiConfig,
      account,
      chainId,
    }),
  })

  return useMarketWalletInfoFromData({
    account,
    balanceData,
    marketInfo,
    isLoading: isMarketInfoPending || isBalancesPending,
  })
}

export function useMarketWalletInfoForMarketQuery({
  chainId,
  marketInfo,
  refetchInterval,
}: MarketWalletInfoForMarketParams): MarketWalletInfo {
  const account = useAccount()
  const wagmiConfig = useConfig()

  const { data: balanceData = [], isPending: isBalancesPending } = useQuery({
    ...marketBalances({
      wagmiConfig,
      account,
      chainId,
    }),
    refetchInterval,
  })

  return useMarketWalletInfoFromData({
    account,
    balanceData,
    marketInfo,
    isLoading: isBalancesPending || !marketInfo,
  })
}

function useMarketWalletInfoFromData({
  account,
  balanceData,
  marketInfo,
  isLoading,
}: {
  account: Address | undefined
  balanceData: BalanceItem[]
  marketInfo: MarketInfo | undefined
  isLoading: boolean
}): MarketWalletInfo {
  const walletBalances: WalletBalance[] = useMemo(
    () =>
      balanceData
        .map((balanceItem) => {
          const token = marketInfo?.findReserveByUnderlyingAsset(balanceItem.address)?.token
          if (!token) {
            return {
              balance: undefined,
              token: undefined,
            }
          }

          return {
            balance: token.fromBaseUnit(balanceItem.balanceBaseUnit),
            token,
          }
        })
        .filter((r) => r.token)
        // @note: this map is here only to make TS happy :(
        .map((r) => ({
          balance: r.balance!,
          token: r.token!,
        })),
    [balanceData, marketInfo],
  )

  const findWalletBalanceForToken = useCallback(
    (token: Token): NormalizedUnitNumber => {
      const aTokenReserve = marketInfo?.findReserveByATokenSymbol(token.symbol)
      if (aTokenReserve) {
        return aTokenReserve.aTokenBalance
      }
      return walletBalances.find((wb) => wb.token.symbol === token.symbol)?.balance ?? NormalizedUnitNumber(0)
    },
    [walletBalances, marketInfo],
  )

  const findWalletBalanceForSymbol = useCallback(
    (symbol: TokenSymbol): NormalizedUnitNumber => {
      const aTokenReserve = marketInfo?.findReserveByATokenSymbol(symbol)
      if (aTokenReserve) {
        return aTokenReserve.aTokenBalance
      }
      return walletBalances.find((wb) => wb.token.symbol === symbol)?.balance ?? NormalizedUnitNumber(0)
    },
    [walletBalances, marketInfo],
  )

  const findWalletBalanceForHyperCoreAsset = useCallback(
    (symbol: TokenSymbol): NormalizedUnitNumber => {
      return findWalletBalanceForSymbol(symbol)
    },
    [findWalletBalanceForSymbol],
  )

  const getTotalUSD = useCallback((): NormalizedUnitNumber => {
    return walletBalances.reduce(
      (acc, { token, balance }) => NormalizedUnitNumber(acc.plus(token.toUSD(balance))),
      NormalizedUnitNumber(0),
    )
  }, [walletBalances])

  const getTotalFormattedUSD = useCallback(
    (options?: Intl.NumberFormatOptions): string => {
      const totalUSD = getTotalUSD()
      const usdFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...options,
      })
      return usdFormatter.format(totalUSD.toNumber())
    },
    [getTotalUSD],
  )

  return useMemo(
    () => ({
      walletBalances,
      isConnected: !!account,
      isLoading,
      findWalletBalanceForToken,
      findWalletBalanceForSymbol,
      findWalletBalanceForHyperCoreAsset,
      getTotalUSD,
      getTotalFormattedUSD,
    }),
    [
      walletBalances,
      account,
      isLoading,
      findWalletBalanceForToken,
      findWalletBalanceForSymbol,
      findWalletBalanceForHyperCoreAsset,
      getTotalUSD,
      getTotalFormattedUSD,
    ],
  )
}
